import { AnyFeedCursor } from '@pressreader/feed-types';
import type { ReaderEntryRecord } from '@pressreader/navigation-core';
import { NavConfig, ViewName } from '@pressreader/navigation-types';

/**
 * The issue view an article entry was opened from: the issue and the language that view asserts for it — a
 * language code, or `null` when the issue is shown in the original. Travels with the entry into browser history,
 * so an entry restored from history still knows which issue language opened it.
 */
interface ArticleOpeningContext {
    issueKey: string;
    language: string | null;
}

/**
 * The view a fullscreen article entry collapses back to. The popup fields are excluded, so a parent captured
 * while a popup was open cannot carry that popup back into itself — nesting stays bounded at one level.
 */
type ArticleReturnToNavConfig = NavConfig & {
    popupArticleConfig?: never;
    popupArticleId?: never;
    aid?: never;
    readerEntry?: never;
};

interface ArticleNavConfig extends NavConfig {
    feedCursor?: AnyFeedCursor | null;

    articleId: string;
    commentKey?: string;

    /** Never on an article entry: the reader parameter belongs to the CONTEXT config the article is shown over. */
    aid?: never;

    /** What the entry remembers about its reader article (cursor, language decision); the same record the context config carries in popup mode. */
    readerEntry?: ReaderEntryRecord;

    lang?: string | null;

    // navigation to issue article
    issueDate?: string;
    country?: string;
    newspaper?: string;

    cid?: string;

    // navigation to post article
    profileId?: string;

    displayMode?: 'view' | 'popup';
    /**
     * Marks the flat landing page: the article page for a visitor from outside, with the site header and footer.
     * Set on entry from outside, kept from article to article, dropped on leaving for another view,
     * so articles opened inside the site never get it.
     */
    flatLandingPage?: boolean;
    context?: NavConfig;

    /** The issue view this entry was opened from; `null` when no issue view opened it. */
    openingContext?: ArticleOpeningContext | null;

    /** The view a fullscreen entry collapses back to; `null` for an entry that has no return parent. */
    returnTo?: ArticleReturnToNavConfig | null;

    notAvailable?: string | boolean;
    // Subview token for the content-not-available fallback; surfaces as `subview-not-available` on the
    // root via useRootClassNames. Set alongside `notAvailable` (which stays the behavioural trigger).
    subview?: 'not-available';
}

function isArticleNavConfig(navConfig: NavConfig | null): navConfig is ArticleNavConfig {
    return navConfig?.viewName === ViewName.Article;
}

/** Whether the entry shows the content-not-available fallback: route params arrive as strings, in-app pushes as booleans. */
function isArticleNotAvailable(navConfig: Pick<ArticleNavConfig, 'notAvailable'> | null | undefined): boolean {
    return navConfig?.notAvailable === true || navConfig?.notAvailable === 'true';
}

/** Takes a view's config as the return target of a fullscreen article entry, dropping the popup and reader fields. */
function toArticleReturnToNavConfig<TNavConfig extends NavConfig>(navConfig: TNavConfig): ArticleReturnToNavConfig {
    const { popupArticleConfig: _0, popupArticleId: _1, aid: _2, readerEntry: _3, ...returnTo } = navConfig as TNavConfig & Record<string, unknown>;

    return returnTo as ArticleReturnToNavConfig;
}

/**
 * Takes an article entry's config as the popup config nested on its parent view. The fields that belong to the
 * entry itself and not to the article — its collapse target, its provenance and its display mode — are dropped,
 * so expanding the popup again builds a fresh entry instead of nesting the previous one inside it.
 */
function toNestedPopupArticleConfig<TArticleNavConfig extends Partial<ArticleNavConfig>>(
    articleNavConfig: TArticleNavConfig,
): Omit<TArticleNavConfig, 'displayMode' | 'openingContext' | 'returnTo'> {
    const { displayMode: _0, openingContext: _1, returnTo: _2, ...popupArticleConfig } = articleNavConfig;

    return popupArticleConfig;
}

export {
    ArticleNavConfig,
    type ArticleOpeningContext,
    type ArticleReturnToNavConfig,
    isArticleNavConfig,
    isArticleNotAvailable,
    toArticleReturnToNavConfig,
    toNestedPopupArticleConfig,
};
